import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { QueueIndicator } from './components/QueueIndicator';
import { getPosition } from './platform/device';
import { ChecklistScreen } from './screens/ChecklistScreen';
import { CompleteScreen } from './screens/CompleteScreen';
import { FoundScreen } from './screens/FoundScreen';
import { RouteScreen } from './screens/RouteScreen';
import { StopScreen } from './screens/StopScreen';
import { API_BASE_FOR_PAGE } from './sync/api';
import { loadRoute, setMeta } from './sync/db';
import { draftFromCompletion, getDraft, newDraft, saveDraft } from './sync/drafts';
import type { StopDraft } from './sync/drafts';
import type { DriverStop } from './sync/route';
import { clearDriverSession } from './sync/session';
import type { DriverSession } from './sync/session';
import { installForegroundTriggers, requestPersistentStorage } from './sync/triggers';
import type { CompletionRecord } from './sync/types';
import { color, muted } from './ui';

/**
 * The Delivery Portal's DRIVER VIEW - /delivery/driver.
 *
 * One screen, one action, no menus (v3 section 9). Screens are a small state
 * machine, not routes: the driver never types a URL. Everything reads and
 * writes the offline queue (./sync); nothing waits on the network.
 *
 * THE BACK BUTTON (Brad, field test 2026-09-14). The screens are a stack, and
 * each step forward - route, stop, checklist, finish - pushes a browser history
 * entry at the same URL, so Android's back button goes back ONE screen instead
 * of leaving the driver view for whatever page came before it. Moving between
 * checklist items is not a step: back from any item returns to the stop.
 */
type Screen =
  | { name: 'route' }
  | { name: 'stop'; stop: DriverStop }
  | { name: 'found'; stop: DriverStop }
  | { name: 'checklist'; stop: DriverStop; draft: StopDraft }
  | { name: 'complete'; stop: DriverStop; draft: StopDraft; mode: 'complete' | 'skip' };

/** The history.state key holding a screen's depth in the stack. */
const DEPTH = 'rbfDriverDepth';

const historyDepth = (state: unknown): number | undefined => {
  const d = (state as Record<string, unknown> | null)?.[DEPTH];
  return typeof d === 'number' ? d : undefined;
};

export default function DriverApp({ session, offline, notice }: { session: DriverSession; offline: boolean; notice: string }) {
  const [stack, setStack] = useState<Screen[]>([{ name: 'route' }]);
  const stackRef = useRef(stack);
  stackRef.current = stack;
  const screen = stack[stack.length - 1];
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // The service worker may not see build-time env; tell it where the API is.
    void setMeta('apiBase', API_BASE_FOR_PAGE);
    void requestPersistentStorage();
    return installForegroundTriggers();
  }, []);

  // The entry the driver view opened on is the route list (depth 0). A back
  // press lands on an entry carrying its depth; trim the stack to it. An entry
  // without a depth is a page outside the driver view - let the router have it.
  useEffect(() => {
    const state = (window.history.state ?? {}) as Record<string, unknown>;
    window.history.replaceState({ ...state, [DEPTH]: 0 }, '');
    const onPop = (e: PopStateEvent) => {
      const depth = historyDepth(e.state);
      if (depth === undefined) return;
      const next = stackRef.current.slice(0, Math.max(0, Math.min(depth, stackRef.current.length - 1)) + 1);
      stackRef.current = next;
      setStack(next);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  /** A step forward: a new screen and a history entry for back to undo. */
  const push = (next: Screen) => {
    const depth = stackRef.current.length;
    const state = (window.history.state ?? {}) as Record<string, unknown>;
    window.history.pushState({ ...state, [DEPTH]: depth }, '');
    stackRef.current = [...stackRef.current, next];
    setStack(stackRef.current);
  };

  /** The same screen with new data (a checklist tap): no history entry. */
  const replace = (next: Screen) => {
    stackRef.current = [...stackRef.current.slice(0, -1), next];
    setStack(stackRef.current);
  };

  /** The on-screen ‹ buttons do exactly what the phone's back button does. */
  const back = () => {
    const s = stackRef.current;
    if (s.length <= 1) return;
    if (historyDepth(window.history.state) === s.length - 1) {
      window.history.back();
    } else {
      stackRef.current = s.slice(0, -1);
      setStack(stackRef.current);
    }
  };

  /** A stop is finished: back to the route, and the stop's screens leave the
   *  back history, so back from the route never reopens a closed stop. */
  const toRoute = () => {
    const depth = stackRef.current.length - 1;
    stackRef.current = [{ name: 'route' }];
    setStack(stackRef.current);
    if (depth > 0 && historyDepth(window.history.state) === depth) window.history.go(-depth);
  };

  const routeId = async () => (await loadRoute<{ id: number }>())?.route.id ?? 0;

  // Arrival = the moment the driver starts the checklist: one position fix,
  // never blocking (getPosition resolves null after 8 s). `at` opens a given
  // item straight away - a row tapped on the stop screen.
  const startChecklist = async (stop: DriverStop, at?: number) => {
    let draft = await getDraft(stop.uuid);
    if (!draft) {
      draft = newDraft(stop.uuid, await routeId(), stop.checklist_required);
      draft.arrivedAt = new Date().toISOString();
      await saveDraft(draft);
      void getPosition().then(async (pos) => {
        if (!pos) return;
        const current = await getDraft(stop.uuid);
        if (current && current.arrivalLat == null) {
          await saveDraft({ ...current, arrivalLat: pos.lat, arrivalLng: pos.lng });
        }
      });
    }
    const required = stop.checklist_required;
    if (at != null && at >= 0 && at < required.length) {
      draft = { ...draft, index: at };
      await saveDraft(draft);
      push({ name: 'checklist', stop, draft });
      return;
    }
    if (!required.length || draft.index >= required.length) {
      push({ name: 'complete', stop, draft, mode: 'complete' });
      return;
    }
    push({ name: 'checklist', stop, draft });
  };

  const skipStop = async (stop: DriverStop) => {
    let draft = await getDraft(stop.uuid);
    if (!draft) {
      draft = newDraft(stop.uuid, await routeId(), stop.checklist_required);
      draft.arrivedAt = new Date().toISOString();
      await saveDraft(draft);
    }
    push({ name: 'complete', stop, draft, mode: 'skip' });
  };

  const fixStop = async (stop: DriverStop, rec: CompletionRecord) => {
    const draft = await draftFromCompletion(rec, stop.checklist_required);
    await saveDraft(draft);
    push(stop.checklist_required.length
      ? { name: 'checklist', stop, draft }
      : { name: 'complete', stop, draft, mode: 'complete' });
  };

  // Sign out clears the COOKIES (backend logout) and who this phone remembers.
  // The upload queue is kept: a stop saved on this phone is never discarded.
  const signOut = async () => {
    await logout();
    await clearDriverSession();
    navigate('/login?next=/delivery/driver', { replace: true });
  };

  let body: React.ReactElement;
  switch (screen.name) {
    case 'route':
      body = (
        <RouteScreen
          onOpenStop={(stop) => push({ name: 'stop', stop })}
          onFixStop={fixStop}
          onSignOut={signOut}
        />
      );
      break;
    case 'stop':
      body = (
        <StopScreen
          stop={screen.stop}
          onStart={() => startChecklist(screen.stop)}
          onStartAt={(i) => startChecklist(screen.stop, i)}
          onSkip={() => skipStop(screen.stop)}
          onFound={() => push({ name: 'found', stop: screen.stop })}
          onBack={back}
        />
      );
      break;
    case 'found':
      body = <FoundScreen stop={screen.stop} onBack={back} />;
      break;
    case 'checklist':
      body = (
        <ChecklistScreen
          stop={screen.stop}
          draft={screen.draft}
          onDraftChange={(draft) => replace({ name: 'checklist', stop: screen.stop, draft })}
          onDone={async () =>
            push({ name: 'complete', stop: screen.stop, draft: (await getDraft(screen.stop.uuid)) ?? screen.draft, mode: 'complete' })}
          onBack={back}
        />
      );
      break;
    case 'complete':
      body = (
        <CompleteScreen
          stop={screen.stop}
          draft={screen.draft}
          mode={screen.mode}
          onDone={toRoute}
          onBack={back}
        />
      );
      break;
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
      <QueueIndicator />
      {(offline || notice) && (
        <div style={{ padding: '8px 16px', background: '#fffbeb', borderBottom: `1px solid ${color.amber}`, fontSize: 16 }}>
          {offline && <div><strong>Offline.</strong> Showing the route saved on this phone for {session.name}.</div>}
          {notice && <div>{notice}</div>}
        </div>
      )}
      {body}
      {screen.name === 'route' && (
        <div style={{ ...muted, textAlign: 'center', padding: '0 16px 110px', fontSize: 14 }}>
          {session.canManage && <><Link to="/delivery/dashboard">Delivery Portal</Link> · </>}
          <Link to="/delivery/driver/device">Device check</Link>
        </div>
      )}
    </div>
  );
}
